'use client';

import { CloseDateSection } from '@/components/common/filter-sections/close-date-section';
import { FilterSection } from '@/components/common/filter-sections/filter-section';
import { MultiSelectSection } from '@/components/common/filter-sections/multi-select-section';
import { OrganizationSection } from '@/components/common/filter-sections/organization-section';
import { SelectSection } from '@/components/common/filter-sections/select-section';
import { StoreDependentSelect } from '@/components/common/filter-sections/store-dependent-select';
import { TextInputSection } from '@/components/common/filter-sections/text-input-section';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { CheckboxCards } from '@/components/ui/checkbox-cards';
import { Input } from '@/components/ui/input';
import { useCommon } from '@/contexts/common-context';
import {
  getPageFilterConfig,
  useFilterStore,
  type FilterState,
  type PageFilterConfig,
} from '@/stores/filter-store';
import { SalesInfoPageOption } from '@/types/enums';
import { ChevronDown, ChevronUp, Filter, X } from 'lucide-react';
import { useEffect, useState } from 'react';

interface FilterBarProps {
  currentPageId?: string;
}

export function FilterBar({ currentPageId = 'default' }: FilterBarProps) {
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  const [filterConfig, setFilterConfig] = useState<PageFilterConfig>(
    getPageFilterConfig(currentPageId)
  );

  // 필요한 필터 값들만 개별적으로 추출 (무한 루프 방지)
  const organization = useFilterStore(state => state.organization);
  const store = useFilterStore(state => state.store);
  const dateRange = useFilterStore(state => state.dateRange);
  const carNumber = useFilterStore(state => state.carNumber);
  const cardNumber = useFilterStore(state => state.cardNumber);
  const approvalNumber = useFilterStore(state => state.approvalNumber);
  const issueCode = useFilterStore(state => state.issueCode);
  const amountRange = useFilterStore(state => state.amountRange);
  const pageOptions = useFilterStore(state => state.pageOptions);
  const salesinfoPageOptions = useFilterStore(
    state => state.salesinfoPageOptions
  );
  const oil = useFilterStore(state => state.oil);
  const dealType = useFilterStore(state => state.dealType);
  const dealer = useFilterStore(state => state.dealer);
  const self = useFilterStore(state => state.self);
  const nozzle = useFilterStore(state => state.nozzle);
  const tank = useFilterStore(state => state.tank);
  const octDateType = useFilterStore(state => state.octDateType);
  const octCStatus = useFilterStore(state => state.octCStatus);
  const octBOption = useFilterStore(state => state.octBOption);
  const cardSalesOptions = useFilterStore(state => state.cardSalesOptions);
  const cardDealer = useFilterStore(state => state.cardDealer);
  const cat = useFilterStore(state => state.cat);
  const approvalType = useFilterStore(state => state.approvalType);
  const isExcludeTankLorry = useFilterStore(state => state.isExcludeTankLorry);
  const carWashIsIncludeRefund = useFilterStore(
    state => state.carWashIsIncludeRefund
  );

  // 액션들
  const updateFilter = useFilterStore(state => state.updateFilter);
  const updateAmountRange = useFilterStore(state => state.updateAmountRange);
  const resetFilters = useFilterStore(state => state.resetFilters);

  // Common context에서 데이터 가져오기
  const { getOils, getDealTypes } = useCommon();

  // 페이지가 변경될 때마다 필터 설정 업데이트
  useEffect(() => {
    const config = getPageFilterConfig(currentPageId);
    setFilterConfig(config);
    // 페이지 변경 시 고급 필터 패널 닫기
    setIsAdvancedOpen(false);

    // 주유소 선택은 persist 되므로, 여러 곳을 고른 상태로 단일 선택 화면에 들어오면
    // 필터바는 여러 곳을 보여주는데 화면은 첫 곳만 조회하게 된다. 진입 시 1곳으로 줄인다.
    if (config.singleStore) {
      const stores = useFilterStore.getState().store;
      if (stores.length > 1) {
        useFilterStore.getState().updateFilter('store', stores.slice(0, 1));
      }
    }
  }, [currentPageId]);

  // 온디맨드 방식: onFiltersChange는 이제 필요없음 (페이지 컴포넌트가 직접 getState() 사용)
  // 필터 UI 피드백만 실시간 동기화 유지

  // 활성화된 필터 개수 계산
  const getActiveFilterCount = (): number => {
    let count = 0;

    // 필수 필터들 체크
    if (organization.length > 0) count++;
    if (store.length > 0) count++;
    if (dateRange.start && dateRange.end) count++;

    // 선택적 필터들 체크
    if (carNumber) count++;
    if (cardNumber) count++;
    if (approvalNumber) count++;
    if (issueCode) count++;
    if (amountRange.min || amountRange.max) count++;
    if (pageOptions.length > 0) count++;
    if (salesinfoPageOptions.length > 0) count++;
    if (oil.length > 0) count++;
    if (dealType.length > 0) count++;
    if (dealer.length > 0) count++;
    if (self.length > 0) count++;
    if (nozzle.length > 0) count++;
    if (tank.length > 0) count++;
    if (octDateType !== 'noDate') count++;
    if (octCStatus !== 'ALL') count++;
    if (octBOption !== 'ALL') count++;

    return count;
  };

  // optional 필터가 있는지 확인
  const hasOptionalFilters = (): boolean => {
    return filterConfig.optional.length > 0;
  };

  // 필터가 해당 페이지에서 표시되어야 하는지 확인
  const shouldShowFilter = (filterKey: keyof FilterState): boolean => {
    return (
      filterConfig.required.includes(filterKey) ||
      filterConfig.optional.includes(filterKey)
    );
  };

  // 필터 초기화
  const handleResetFilters = () => {
    resetFilters();
  };

  // 날짜 프리셋 처리
  const handleDatePreset = (days: number) => {
    const today = new Date();
    let startDate: Date;
    let endDate: Date;

    if (days === 0) {
      // 오늘
      startDate = new Date(today);
      endDate = new Date(today);
    } else if (days === 1) {
      // 어제
      startDate = new Date(today);
      startDate.setDate(today.getDate() - 1);
      endDate = new Date(startDate);
    } else {
      // 1주, 2주, 한달 등
      endDate = new Date(today);
      startDate = new Date(today);
      startDate.setDate(today.getDate() - days);
    }

    // YYYY-MM-DD 형식으로 변환
    const formatDate = (date: Date): string => {
      return date.toISOString().split('T')[0];
    };

    updateFilter('dateRange', {
      start: formatDate(startDate),
      end: formatDate(endDate),
    });
  };

  // 금액 범위 검증 함수 (자동 조정 제거)
  const isAmountRangeValid = (min: string, max: string): boolean => {
    const minNum = parseFloat(min) || 0;
    const maxNum = parseFloat(max) || 0;

    // 둘 다 입력된 경우에만 검증
    if (min && max) {
      return minNum <= maxNum;
    }

    return true; // 하나만 입력된 경우는 유효
  };

  // 금액 범위 업데이트 함수 (단순 업데이트만)
  const handleAmountRangeChange = (type: 'min' | 'max', value: string) => {
    if (type === 'min') {
      updateAmountRange(value, amountRange.max);
    } else {
      updateAmountRange(amountRange.min, value);
    }
  };

  const activeFilterCount = getActiveFilterCount();

  // 필터가 비활성화된 페이지에서는 버튼을 숨김
  if (filterConfig.disabled) {
    return null;
  }

  return (
    <div className='border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60'>
      {/* 기본 필터 바 */}
      <div className='p-3 border-b border-border/50'>
        {/* 필터 항목들과 액션 버튼들 */}
        <div className='flex items-start justify-between'>
          <div className='flex items-start gap-6'>
            {/* 조직 필터 */}
            {shouldShowFilter('organization') && (
              <div>
                <div className='flex items-center justify-between gap-2'>
                  <span className='text-xs font-medium text-muted-foreground'>
                    조직
                  </span>
                </div>
                <OrganizationSection singleStore={filterConfig.singleStore} />
              </div>
            )}

            {/* 기간 필터 */}
            {shouldShowFilter('dateRange') && (
              <div>
                <div className='flex items-center justify-between gap-2'>
                  <span className='text-xs font-medium text-muted-foreground'>
                    기간
                  </span>
                  <div className='flex items-center gap-1'>
                    <Button
                      variant='outline'
                      size='sm'
                      className='h-4 px-2 text-xs'
                      onClick={() => handleDatePreset(0)}
                    >
                      오늘
                    </Button>
                    <Button
                      variant='outline'
                      size='sm'
                      className='h-4 px-2 text-xs'
                      onClick={() => handleDatePreset(1)}
                    >
                      어제
                    </Button>
                    <Button
                      variant='outline'
                      size='sm'
                      className='h-4 px-2 text-xs'
                      onClick={() => handleDatePreset(7)}
                    >
                      1주
                    </Button>
                    <Button
                      variant='outline'
                      size='sm'
                      className='h-4 px-2 text-xs'
                      onClick={() => handleDatePreset(14)}
                    >
                      2주
                    </Button>
                    <Button
                      variant='outline'
                      size='sm'
                      className='h-4 px-2 text-xs'
                      onClick={() => handleDatePreset(30)}
                    >
                      한달
                    </Button>
                  </div>
                </div>
                <div className='flex items-center gap-1'>
                  <Input
                    type='date'
                    value={dateRange.start}
                    onChange={e =>
                      updateFilter('dateRange', {
                        ...dateRange,
                        start: e.target.value,
                      })
                    }
                    className=''
                    inputSize='sm'
                  />
                  <span className='text-xs text-muted-foreground'>~</span>
                  <Input
                    type='date'
                    value={dateRange.end}
                    onChange={e =>
                      updateFilter('dateRange', {
                        ...dateRange,
                        end: e.target.value,
                      })
                    }
                    className=''
                    inputSize='sm'
                  />
                </div>
              </div>
            )}
            {/* 마감일자·마감차수 - 선택한 주유소의 마감 이력에서 값을 받아온다 */}
            {shouldShowFilter('closeDate') && (
              <div>
                <div className='flex items-center justify-between gap-2'>
                  <span className='text-xs font-medium text-muted-foreground'>
                    마감
                  </span>
                </div>
                <CloseDateSection />
              </div>
            )}
          </div>

          {/* 액션 버튼들 */}
          <div className='flex items-center gap-2'>
            {activeFilterCount > 0 && (
              <Badge variant='secondary' className='h-6 px-2 text-xs'>
                {activeFilterCount}
              </Badge>
            )}

            <Button
              variant='ghost'
              size='sm'
              onClick={handleResetFilters}
              className='h-7 px-2 text-xs'
              title='모든 필터 초기화'
            >
              <X className='h-3 w-3' />
            </Button>

            {hasOptionalFilters() && (
              <Button
                variant='outline'
                size='sm'
                className='h-7 px-3 text-xs'
                onClick={() => setIsAdvancedOpen(!isAdvancedOpen)}
              >
                <Filter className='h-3 w-3 mr-1' />
                상세 필터
                {isAdvancedOpen ? (
                  <ChevronUp className='h-3 w-3 ml-1' />
                ) : (
                  <ChevronDown className='h-3 w-3 ml-1' />
                )}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* 고급 필터 패널 - 기본 필터 아래 세로로 배치 */}
      {isAdvancedOpen && (
        <div className='bg-muted/20 border-b border-border/50'>
          <div className='px-4 py-4'>
            <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4'>
              {/* 페이지 옵션 */}
              {shouldShowFilter('salesinfoPageOptions') && (
                <FilterSection title='페이지 옵션'>
                  <MultiSelectSection
                    values={salesinfoPageOptions}
                    onValuesChange={values =>
                      updateFilter(
                        'salesinfoPageOptions',
                        values as (keyof typeof SalesInfoPageOption)[]
                      )
                    }
                    options={Object.keys(SalesInfoPageOption).map(key => ({
                      value: key,
                      label:
                        SalesInfoPageOption[
                          key as keyof typeof SalesInfoPageOption
                        ],
                    }))}
                    placeholder='페이지 옵션 선택'
                    size='sm'
                  />
                </FilterSection>
              )}

              {/* 유종 */}
              {shouldShowFilter('oil') && (
                <FilterSection title='유종'>
                  <MultiSelectSection
                    values={oil}
                    onValuesChange={values => updateFilter('oil', values)}
                    options={getOils().map(oil => ({
                      value: oil.oilCode || '',
                      label: oil.oilName || '',
                    }))}
                    placeholder='유종 선택'
                    size='sm'
                  />
                </FilterSection>
              )}

              {/* 탱크 */}
              {shouldShowFilter('tank') && (
                <FilterSection title='탱크'>
                  <TextInputSection
                    value={tank.join(',')}
                    onChange={value =>
                      updateFilter('tank', value.split(',') as string[])
                    }
                    placeholder='탱크 입력(콤마로 구분)'
                    size='sm'
                  />
                </FilterSection>
              )}

              {/* 노즐 */}
              {shouldShowFilter('nozzle') && (
                <FilterSection title='노즐'>
                  <TextInputSection
                    value={nozzle.join(',')}
                    onChange={value =>
                      updateFilter('nozzle', value.split(',') as string[])
                    }
                    placeholder='노즐 입력(콤마로 구분)'
                    size='sm'
                  />
                </FilterSection>
              )}

              {/* 셀프 ODT 번호 */}
              {shouldShowFilter('self') && (
                <FilterSection title='ODT'>
                  <TextInputSection
                    value={self.join(',')}
                    onChange={value =>
                      updateFilter('self', value.split(',') as string[])
                    }
                    placeholder='ODT 입력(콤마로 구분)'
                    size='sm'
                  />
                </FilterSection>
              )}

              {/* 거래유형 */}
              {shouldShowFilter('dealType') && (
                <FilterSection title='판매구분'>
                  <MultiSelectSection
                    values={dealType}
                    onValuesChange={values => updateFilter('dealType', values)}
                    options={getDealTypes().map(dealType => ({
                      value: dealType.typeCode || '',
                      label: dealType.typeCode + ' ' + dealType.typeName || '',
                    }))}
                    placeholder='판매구분 선택'
                    showScroll={true}
                    size='sm'
                  />
                </FilterSection>
              )}

              {/* 카드매출 옵션 */}
              {shouldShowFilter('cardSalesOptions') && (
                <FilterSection title='카드매출 옵션'>
                  <MultiSelectSection
                    values={cardSalesOptions}
                    onValuesChange={values =>
                      updateFilter('cardSalesOptions', values)
                    }
                    options={[
                      { value: 'neTruck', label: '네트럭/영업화물우대' },
                      { value: 'inMoney', label: '유대입금' },
                      { value: 'cashBill', label: '현금소득' },
                    ]}
                    placeholder='옵션 선택'
                    size='sm'
                  />
                </FilterSection>
              )}

              {/* 카드거래처 - 단일 주유소 선택 시에만 활성화 */}
              {shouldShowFilter('cardDealer') && (
                <FilterSection title='카드거래처'>
                  <StoreDependentSelect
                    type='cardDealer'
                    values={cardDealer}
                    onValuesChange={values =>
                      updateFilter('cardDealer', values as string[])
                    }
                    placeholder='카드거래처 선택'
                    multiSelect={true}
                    size='sm'
                  />
                </FilterSection>
              )}

              {/* 단말기ID - 단일 주유소 선택 시에만 활성화 */}
              {shouldShowFilter('cat') && (
                <FilterSection title='단말기ID'>
                  <StoreDependentSelect
                    type='cat'
                    values={cat}
                    onValuesChange={value =>
                      updateFilter('cat', value as string)
                    }
                    placeholder='단말기ID 선택'
                    multiSelect={false}
                    size='sm'
                  />
                </FilterSection>
              )}

              {/* 승인 타입 */}
              {shouldShowFilter('approvalType') && (
                <FilterSection title='승인 타입'>
                  <SelectSection
                    value={approvalType}
                    onValueChange={value =>
                      updateFilter(
                        'approvalType',
                        value as 'ALL' | 'CONFIRM' | 'CANCEL'
                      )
                    }
                    options={[
                      { value: 'ALL', label: '전체' },
                      { value: 'CONFIRM', label: '승인' },
                      { value: 'CANCEL', label: '취소' },
                    ]}
                    placeholder='승인 타입 선택'
                    size='sm'
                  />
                </FilterSection>
              )}

              {/* 거래처 - 단일 주유소 선택 시에만 활성화 */}
              {shouldShowFilter('dealer') && (
                <FilterSection title='거래처'>
                  <StoreDependentSelect
                    type='dealer'
                    values={dealer}
                    onValuesChange={values =>
                      updateFilter('dealer', values as string[])
                    }
                    placeholder='거래처 선택'
                    multiSelect={true}
                    size='sm'
                  />
                </FilterSection>
              )}

              {/* 금액 범위 */}
              {shouldShowFilter('amountRange') && (
                <FilterSection title='금액 범위'>
                  <div className='grid grid-cols-2 gap-2'>
                    <TextInputSection
                      value={amountRange.min}
                      onChange={value => handleAmountRangeChange('min', value)}
                      placeholder='최소 금액'
                      type='number'
                      size='sm'
                    />
                    <TextInputSection
                      value={amountRange.max}
                      onChange={value => handleAmountRangeChange('max', value)}
                      placeholder='최대 금액'
                      type='number'
                      size='sm'
                    />
                  </div>
                  {/* 검증 경고 메시지 표시 */}
                  {amountRange.min &&
                    amountRange.max &&
                    !isAmountRangeValid(amountRange.min, amountRange.max) && (
                      <div className='mt-1 text-xs text-orange-600'>
                        ⚠️ 최소값이 최대값보다 클 수 없습니다. 올바른 범위를
                        입력해주세요.
                      </div>
                    )}
                </FilterSection>
              )}

              {/* 차량번호 */}
              {shouldShowFilter('carNumber') && (
                <FilterSection title='차량번호'>
                  <TextInputSection
                    value={carNumber}
                    onChange={value => updateFilter('carNumber', value)}
                    placeholder='차량번호 입력'
                    size='sm'
                  />
                </FilterSection>
              )}

              {/* 카드번호 */}
              {shouldShowFilter('cardNumber') && (
                <FilterSection title='카드번호'>
                  <TextInputSection
                    value={cardNumber}
                    onChange={value => updateFilter('cardNumber', value)}
                    placeholder='카드번호 입력'
                    type='number'
                    size='sm'
                  />
                </FilterSection>
              )}

              {/* 승인번호 */}
              {shouldShowFilter('approvalNumber') && (
                <FilterSection title='승인번호'>
                  <TextInputSection
                    value={approvalNumber}
                    onChange={value => updateFilter('approvalNumber', value)}
                    placeholder='승인번호 입력'
                    type='number'
                    size='sm'
                  />
                </FilterSection>
              )}

              {/* 보관증번호 */}
              {shouldShowFilter('issueCode') && (
                <FilterSection title='보관증번호'>
                  <TextInputSection
                    value={issueCode}
                    onChange={value => updateFilter('issueCode', value)}
                    placeholder='보관증번호 입력'
                    size='sm'
                  />
                </FilterSection>
              )}

              {/* 탱크로리 제외 여부 */}
              {shouldShowFilter('isExcludeTankLorry') && (
                <FilterSection title='&nbsp;'>
                  <CheckboxCards
                    options={[
                      { value: 'isExcludeTankLorry', label: '탱크로리 제외' },
                    ]}
                    selectedValues={
                      isExcludeTankLorry ? ['isExcludeTankLorry'] : []
                    }
                    onValueChange={(values: string[]) =>
                      updateFilter('isExcludeTankLorry', values.length > 0)
                    }
                    size='sm'
                    variant='surface'
                  />
                </FilterSection>
              )}

              {/* 환불 포함 여부 */}
              {shouldShowFilter('carWashIsIncludeRefund') && (
                <FilterSection title='&nbsp;'>
                  <CheckboxCards
                    options={[
                      { value: 'carWashIsIncludeRefund', label: '환불 포함' },
                    ]}
                    selectedValues={
                      carWashIsIncludeRefund ? ['carWashIsIncludeRefund'] : []
                    }
                    onValueChange={(values: string[]) =>
                      updateFilter('carWashIsIncludeRefund', values.length > 0)
                    }
                    size='sm'
                    variant='surface'
                  />
                </FilterSection>
              )}

            </div>
          </div>
        </div>
      )}
    </div>
  );
}
